import { Grid } from '@/common/components/Grid/Grid';
import { Stack } from '@/common/components/Stack/Stack';
import { PolicyOperatorDetailsCard } from '@/common/containers/Dashboard/Insurance/InsuranceHomePage/InsurancePolicyDetail/PolicyOperatorDetailsCard/PolicyOperatorDetailsCard';
import {
  useGetInsurancePolicies,
  useGetInsurancePolicyOperator,
} from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import { IInsurancePolicy } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { canAddFamilyMembersToPolicy } from '@/common/integrations/menhir/employee/insurance/insurance.utils';
import { useGetSpotlights } from '@/common/integrations/menhir/employee/spotlights/spotlights.hooks';
import { useShouldDisplayFamilyMembersUI } from '@/web/containers/Insurance/InsuranceUpgrade/common/utils';
import { useInsurancePolicyAlert } from './InsurancePolicyDetails.utils';
import { PolicyActivationAlert } from './PolicyActivationAlert/PolicyActivationAlert';
import { PolicyCoverageCard } from './PolicyCoveragesCard/PolicyCoveragesCard';
import { PolicyDetailsCard } from './PolicyDetailsCard/PolicyDetailsCard';
import { PolicyPeopleList } from './PolicyPeopleList/PolicyPeopleList';
import { PolicySummaryCard } from './PolicySummaryCard/PolicySummaryCard';
import { SimulateFamilyMembersCTA } from './SimulateFamilyMembersCTA/SimulateFamilyMembersCTA';

export type TInsurancePolicyDetailProps = {
  policy: IInsurancePolicy;
};

export function InsurancePolicyDetail({ policy }: TInsurancePolicyDetailProps) {
  const { spotlights } = useGetSpotlights();
  const { shouldDisplayFamilyMembersUI } = useShouldDisplayFamilyMembersUI();
  const displayFamilyMembersUI = shouldDisplayFamilyMembersUI(policy);
  const { upcomingPolicy } = useGetInsurancePolicies();
  const { operator, isLoading, isError } = useGetInsurancePolicyOperator(
    policy.id
  );

  const alert = useInsurancePolicyAlert(policy);

  const showAddMemberBox = canAddFamilyMembersToPolicy(
    policy,
    displayFamilyMembersUI
  );
  const isUpcomingPolicy = policy.status === 'drafted';
  const upgradeEnded = !spotlights?.insurance?.upgradePeriod?.active;

  const canAddFamilyMembers =
    !isUpcomingPolicy &&
    !spotlights?.insurance?.upgradePeriod &&
    !upcomingPolicy &&
    showAddMemberBox;

  const hasActivePersons = policy.persons.some(
    (person) => person.status === 'active'
  );

  const shouldShowOperatorCard =
    !isError &&
    (isLoading || operator?.phone || operator?.email || operator?.url);

  return (
    <Stack gap="page">
      {isUpcomingPolicy && (
        <PolicyActivationAlert
          startDate={policy.startsOn}
          upgradeEnded={upgradeEnded}
        />
      )}
      {hasActivePersons && <PolicySummaryCard policy={policy} />}
      <PolicyPeopleList policy={policy} />
      {alert}
      {canAddFamilyMembers && <SimulateFamilyMembersCTA policy={policy} />}
      <Grid columnBasis="basis-[30rem]" gap="page" hideExtraCells>
        {shouldShowOperatorCard && (
          <PolicyOperatorDetailsCard operator={operator} loading={isLoading} />
        )}
        <PolicyDetailsCard policy={policy} />
        <PolicyCoverageCard policy={policy} />
      </Grid>
    </Stack>
  );
}
